import React, { useMemo } from 'react';

import { Box, IconButton, Typography } from '@mui/material';
import type {
  GridColDef,
  GridGroupingColDefOverride,
  GridRenderCellParams,
  GridRowId,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import {
  gridColumnLookupSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import type { DisplayMode, ReportRow } from 'src/components';
import { ArrowRightIcon } from 'src/components/icons/ArrowRightIcon';
import { ExpandIcon } from 'src/components/icons/ExpandIcon';
import { getIsGroupHeaderRow } from 'src/components/reports/types';
import { GroupHeaderRowCell } from 'src/components/shared/GroupHeaderRowCell';
import { getIsRowExpanded } from 'src/hooks/data-grid-premium';

import type { GroupableColumn } from './useGroupableColumns';

interface UseGroupingColumnsOptions {
  displayMode: DisplayMode;
  renderLeafCell: (cellParams: GridRenderCellParams<ReportRow>, depth: number) => React.ReactNode;
  rowGroupingModel: string[];
  toggleRowExpansion: (rowId: GridRowId) => void;
  /**
   * Groupable columns the grid defines no column for, such as the extraction
   * questions the repository grid groups by without showing. The grouping
   * header takes a field's label, and a group header row its column type,
   * from here when the grid's column lookup has nothing for the field.
   */
  virtualGroupableColumns?: readonly GroupableColumn[];
}

/** The part of the grid's column lookup the grouping header reads. */
interface GroupingColumnLookup {
  readonly [field: string]: Pick<GridColDef, 'headerName'> | undefined;
}

interface GroupingHeaderSelectorArgs {
  rowGroupingModel: string[];
  virtualColumnsByField: ReadonlyMap<string, GroupableColumn>;
}

/**
 * The grouped columns' labels as one string, e.g. "Status, Payment Terms".
 *
 * A column the grid defines is named by the grid. One it does not define is
 * named by the virtual columns. Anything else keeps its raw field, so a
 * grouping that outlived its column still reads as something rather than
 * nothing.
 */
export function getGroupingHeaderLabels(
  rowGroupingModel: readonly string[],
  columnLookup: GroupingColumnLookup,
  virtualColumnsByField: ReadonlyMap<string, GroupableColumn>
): string {
  return rowGroupingModel
    .map(
      field => columnLookup[field]?.headerName || virtualColumnsByField.get(field)?.label || field
    )
    .join(', ');
}

/**
 * The grouped columns' header labels, as the one string the header renders.
 *
 * Selecting the finished label rather than the column lookup keeps the header
 * out of every column-state update: the lookup takes a new identity on each
 * resize frame, visibility toggle and reorder, while this string only changes
 * when a grouped column's name does.
 */
function selectGroupingHeaderLabels(
  apiRef: Parameters<typeof gridColumnLookupSelector>[0],
  { rowGroupingModel, virtualColumnsByField }: GroupingHeaderSelectorArgs
): string {
  return getGroupingHeaderLabels(
    rowGroupingModel,
    gridColumnLookupSelector(apiRef),
    virtualColumnsByField
  );
}

/**
 * Header for the row-grouping column, e.g. "Group by (Status, Payment Terms)".
 *
 * Resolves each field's label from the grid's own column definitions rather
 * than title-casing the raw field: a grouping field can be an extraction
 * question UUID, which has no readable form of its own. Rendered inside the
 * grid so the column lookup is available and stays current as columns load.
 */
const GroupingColumnHeader = React.memo(function GroupingColumnHeader({
  rowGroupingModel,
  virtualColumnsByField,
}: GroupingHeaderSelectorArgs) {
  const apiRef = useGridApiContext();
  // `useGridSelector` compares a plain-object argument key by key, so this literal needs no memo.
  const labels = useGridSelector(apiRef, selectGroupingHeaderLabels, {
    rowGroupingModel,
    virtualColumnsByField,
  });

  return <Typography variant="body2">{`Group by (${labels})`}</Typography>;
});

/**
 * Returns a `groupingColDef` for DataGridPremium that handles both relationship
 * (tree data) and row-grouping display modes.
 *
 * - **Flat mode** (`displayMode: 'flat'` with empty `rowGroupingModel`): returns `undefined`
 *   so the grid renders its default columns.
 * - **Row-grouping mode** (`rowGroupingModel` has entries): returns a grouping column
 *   where group header rows render an expand/collapse toggle (via `GroupHeaderRowCell`)
 *   and leaf (document) rows are rendered by the caller-provided `renderLeafCell`.
 * - **Relationship mode** (`displayMode: 'relationship'`): returns a full-width column
 *   that renders the expand/collapse toggle inline with the document name cell, similar
 *   to MUI's default tree data layout.
 */
export function useGroupingColumns({
  displayMode,
  renderLeafCell,
  rowGroupingModel,
  toggleRowExpansion,
  virtualGroupableColumns,
}: UseGroupingColumnsOptions): GridGroupingColDefOverride<ReportRow> | undefined {
  const virtualColumnsByField = useMemo(
    () =>
      new Map(
        virtualGroupableColumns?.map((column): [string, GroupableColumn] => [column.field, column])
      ),
    [virtualGroupableColumns]
  );

  return useMemo(() => {
    if (displayMode === 'relationship') {
      return {
        /**
         * Matches the flat-mode Document column's share of the row, plus room for
         * the expand toggle and one indent per nesting level, which the flat
         * column doesn't have to fit.
         */
        flex: 2.5,
        headerName: 'Document Name',
        hideDescendantCount: true,
        leafField: 'name',
        minWidth: 400,
        renderCell: (cellParams: GridRenderCellParams<ReportRow>) => {
          const depth = cellParams.rowNode.depth;
          const isExpanded = getIsRowExpanded(cellParams.rowNode);
          const hasChildren =
            cellParams.rowNode.type === 'group' && hasEffectiveChildren(cellParams.row);

          const handleToggle = (event: React.MouseEvent) => {
            event.stopPropagation();
            toggleRowExpansion(cellParams.id);
          };

          return (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
                p: 1,
                pl: depth * 2 + 1,
                width: '100%',
              }}
            >
              {hasChildren ? (
                <IconButton
                  aria-label={isExpanded ? 'hide children' : 'see children'}
                  size="small"
                  sx={{ flexShrink: 0 }}
                  onClick={handleToggle}
                >
                  {isExpanded ? <ExpandIcon /> : <ArrowRightIcon />}
                </IconButton>
              ) : (
                <Box sx={{ flexShrink: 0, width: 28 }} />
              )}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                }}
              >
                {renderLeafCell(cellParams, depth)}
              </Box>
            </Box>
          );
        },
      } satisfies GridGroupingColDefOverride<ReportRow>;
    }

    if (rowGroupingModel.length) {
      return {
        flex: 1,
        // Fallback only: `renderHeader` resolves the real column labels.
        headerName: 'Group by',
        hideDescendantCount: true,
        leafField: 'name',
        minWidth: 400,
        renderCell: (cellParams: GridRenderCellParams) => {
          const row = cellParams.row;
          if (!getIsGroupHeaderRow(row)) {
            const leafDepth = cellParams.rowNode.depth;
            return (
              <Box
                sx={{
                  display: 'flex',
                  pl: leafDepth * 2,
                }}
              >
                {renderLeafCell(cellParams, leafDepth)}
              </Box>
            );
          }

          return (
            <GroupHeaderRowCell
              cellParams={cellParams}
              fallbackColumnType={virtualColumnsByField.get(row.groupFieldId)?.type}
              toggleRowExpansion={toggleRowExpansion}
            />
          );
        },
        renderHeader: () => (
          <GroupingColumnHeader
            rowGroupingModel={rowGroupingModel}
            virtualColumnsByField={virtualColumnsByField}
          />
        ),
      } satisfies GridGroupingColDefOverride<ReportRow>;
    }

    return undefined;
  }, [displayMode, renderLeafCell, rowGroupingModel, toggleRowExpansion, virtualColumnsByField]);
}

/**
 * MUI sets `rowNode.type === 'group'` once during fetch and never updates it
 * via `updateRows`. When all children are removed (in unconfirmed-only or
 * missing-matches mode), we set `hasVisibleChildren: false` on the row data
 * to hide the expand icon. When the flag is absent, we trust MUI's default.
 */
function hasEffectiveChildren(row: GridValidRowModel): boolean {
  return row.hasVisibleChildren !== false;
}
